import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button, message} from 'antd';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ReportFileInitialValue} from "./config/reportFileFilterOptions";
import {ReportFileType} from "./graphql/queries/getReportFileListingForCentral";
import {useCentralReportFileQuery, ReportFileFiltersType} from "./hooks/useCentralReportFileQuery";
import CentralReportFileFilter from "./components/CentralReportFileFilter";
import CentralReportFileColumns from "./components/CentralReportFileColumns";
import {getLastWeekDateRange} from "../../../utils/dateTimeUtils";
import TablePaginationBar from '../../../components/TablePaginationBar';
import {useLazyQuery} from '@apollo/client';
import {
    GET_BATCH_REPORT_DOWNLOAD_URLS,
    GetBatchReportDownloadUrlsResponse,
    GetBatchReportDownloadUrlsVariables
} from './graphql/queries/getBatchReportFileUrls';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';

const CentralReportFilePage: React.FC = () => {
    const {t} = useTranslation();
    const location = useLocation();
    const {currentWarehouse} = useWarehouseContext();
    const [tempFilters, setTempFilters] = useState<ReportFileFiltersType>(ReportFileInitialValue);
    const [filters, setFilters] = useState<ReportFileFiltersType>(ReportFileInitialValue);
    const initializedRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportFileType[]>([]);
    const [downloadLoading, setDownloadLoading] = useState(false);
    
    // 下载中心页面已无操作人下拉框，且后端 getReportFileListingForPickup 会强制 operatorId=self，
    // 默认过滤条件不再依赖查询操作人列表，直接同步给出
    const operatorsLoading = false;
    const defaultFilters = useMemo<ReportFileFiltersType>(() => ({
        type: undefined,
        name: undefined,
        reportDate: getLastWeekDateRange(),
        operatorId: undefined,
    }), []);
    
    // 计算是否应该跳过查询：defaultFilters 未加载完成时跳过
    const shouldSkipQuery = !defaultFilters || !initializedRef.current;
    
    // 只有在操作人数据加载完成后才执行报表查询
    // 默认日期不写入 URL/hash：只在页面加载时计算一次，避免每次 render 都生成新时间导致无限 refetch
    const defaultReportDate = useMemo(() => getLastWeekDateRange(), []);

    const effectiveFilters = useMemo(() => {
        return {
            ...filters,
            reportDate: filters.reportDate === undefined ? defaultReportDate : filters.reportDate,
        };
    }, [filters, defaultReportDate]);

    const {
        reportFileData,
        loading: reportLoading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useCentralReportFileQuery(effectiveFilters, currentPage, pageSize, shouldSkipQuery, currentWarehouse?.id);

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: ReportFileType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const [getBatchDownloadUrls] = useLazyQuery<
        GetBatchReportDownloadUrlsResponse,
        GetBatchReportDownloadUrlsVariables
    >(GET_BATCH_REPORT_DOWNLOAD_URLS);

    // 当默认过滤条件加载完成时，初始化过滤器状态
    useEffect(() => {
        if (defaultFilters && !initializedRef.current) {
            let initialFilters = defaultFilters;

            // 从URL参数中获取type值
            const searchParams = new URLSearchParams(location.search);
            const urlType = searchParams.get('type');
            const typeFromUrl = urlType ? parseInt(urlType, 10) : undefined;

            // 如果URL中存在type参数且是有效数字，则使用URL中的值
            if (typeFromUrl !== undefined && !isNaN(typeFromUrl)) {
                initialFilters = {...initialFilters, type: typeFromUrl};
            }

            setTempFilters(initialFilters);
            setFilters(initialFilters);
            initializedRef.current = true;
        }
    }, [defaultFilters, location.search]);



    const handleResetFilters = () => {
        const resetFilters = defaultFilters || {
            ...ReportFileInitialValue,
            // 默认日期不写入 URL/hash：保持 undefined，由查询层补齐默认值
            reportDate: undefined
        };
        setTempFilters(resetFilters);
        setFilters(resetFilters);
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    // 添加useEffect监听状态变化后执行查询，跳过不符合条件的查询
    useEffect(() => {
        // 如果 defaultFilters 还未加载完成，跳过查询
        if (!defaultFilters) {
            return;
        }
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize, defaultFilters]);
    
    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const generateFilename = (index: number, reportName?: string) => {
        const timestamp = new Date().toISOString().split('T')[0];
        if (reportName) {
            const hasXlsx = /\.xlsx$/i.test(reportName);
            return hasXlsx ? reportName : `${reportName}.xlsx`;
        }
        return `Report_${index + 1}_${timestamp}.xlsx`;
    };

    const downloadFile = async (url: string, filename: string) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);
    };

    const handleBatchDownload = async () => {
        if (selectedRows.length === 0) {
            message.warning('Please select the report you wish to download');
            return;
        }
        if (!currentWarehouse?.id) {
            message.error('Warehouse not ready, please try again later');
            return;
        }

        setDownloadLoading(true);
        try {
            const reportIds = selectedRows.map(report => parseInt(report.id.toString(), 10));
            const { data } = await getBatchDownloadUrls({
                variables: { reportIds, warehouseId: currentWarehouse.id },
                fetchPolicy: 'network-only'
            });

            if (!data?.getBatchReportDownloadUrlsForPickup) {
                message.error('Failed to obtain the download link.');
                return;
            }

            const downloadUrls = data.getBatchReportDownloadUrlsForPickup;
            if (downloadUrls.length === 0) {
                message.warning('No available download link is available.');
                return;
            }

            if (downloadUrls.length > 1) {
                message.info(`Starting batch download of ${downloadUrls.length} files...`);
            }

            for (let i = 0; i < downloadUrls.length; i++) {
                const url = downloadUrls[i];
                const reportName = i < selectedRows.length ? selectedRows[i].name : undefined;
                const filename = generateFilename(i, reportName);

                try {
                    await downloadFile(url, filename);
                    await new Promise(res => setTimeout(res, 500));
                    message.success(`File ${i + 1} downloaded`);
                } catch (e) {
                    console.log('Failed to download file', e);
                    message.error(`Failed to download file ${i + 1}`);
                }
            }

            message.success(`All ${downloadUrls.length} files download initiated successfully!`);
        } catch (error) {
            console.error('Batch download failed:', error);
            message.error('Batch download failed, please try again later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">

                {/* 筛选条件 */}
                <CentralReportFileFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                />

                {/* 按钮区域 */}
                <div className="flex justify-end gap-2 mb-3">
                    <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                        <span className="font-bold">{t('central.download.filters.filter')}</span>
                    </Button>
                    <Button onClick={handleResetFilters} className="w-auto min-w-24">
                        <span className="font-bold">{t('central.download.filters.reset')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleBatchDownload}
                        loading={downloadLoading}
                        disabled={selectedRows.length === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">
                            {t('central.download.filters.batchDownload')} ({selectedRows.length})
                        </span>
                    </Button>
                </div>

                {/* 报表列表 */}
                <CentralReportFileColumns
                    reportFileData={reportFileData}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={operatorsLoading || reportLoading}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                />
            </div>
    );
};

export default CentralReportFilePage;